import { useRef, useState } from "react";
import { UploadCloud, Download, RotateCcw, Database, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../store/dataStore";
import { ingestFile, downloadTemplate, SCHEMAS } from "../ingest/ingest";

export default function DataManager() {
  const { tables, source, setTables, resetToSample } = useData();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lastLoad, setLastLoad] = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const parsed = await ingestFile(file);
      setTables(parsed);
      const summary = Object.entries(parsed).map(([k, v]) => `${k}: ${v.length} rows`);
      setLastLoad(summary);
      toast.success(`Loaded ${Object.keys(parsed).length} table(s) from ${file.name}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>Data</h2>
        <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
          The contract: fill the leaf grain, aggregations derive automatically. Upload a CSV (one fact table) or a multi-sheet XLSX where sheet names match fact table names.
        </p>
      </div>

      <div
        className="border-2 border-dashed rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer transition-colors"
        style={{ borderColor: "var(--line)", background: "var(--white)" }}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
        data-testid="upload-zone"
      >
        <UploadCloud size={36} style={{ color: "var(--teal-d)" }} />
        <div className="font-bold mt-3" style={{ color: "var(--navy)" }}>{busy ? "Parsing…" : "Drop CSV / XLSX here or click to browse"}</div>
        <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>Headers are auto-classified to fact tables by schema signature</div>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="file-input" onChange={(e) => onFile(e.target.files[0])} />
      </div>

      <div className="flex gap-3 flex-wrap">
        <button className="btn-util" data-testid="btn-template" onClick={() => downloadTemplate(tables)}>
          <Download size={13} /> Download XLSX template
        </button>
        <button className="btn-util" data-testid="btn-reset-sample" onClick={() => { resetToSample(); setLastLoad(null); toast.success("Restored sample dataset"); }}>
          <RotateCcw size={13} /> Reset to sample data
        </button>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md" style={{ background: source === "sample" ? "var(--bg)" : "var(--good)", color: source === "sample" ? "var(--slate)" : "#fff" }} data-testid="data-source-badge">
          <Database size={12} /> Source: {source === "sample" ? "Sample (generated leaf facts)" : "Uploaded"}
        </span>
      </div>

      {lastLoad && (
        <div className="panel p-4 text-sm" data-testid="load-summary">
          <div className="flex items-center gap-2 font-bold mb-2" style={{ color: "var(--good)" }}><CheckCircle2 size={15} /> Last upload</div>
          {lastLoad.map((s) => <div key={s} className="num text-xs" style={{ color: "var(--navy)" }}>{s}</div>)}
        </div>
      )}

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="tables-summary">
          <thead><tr><th>Table</th><th>Grain (leaf record)</th><th className="text-right">Rows loaded</th><th>Schema signature</th></tr></thead>
          <tbody>
            {Object.entries(SCHEMAS).map(([name, cols]) => (
              <tr key={name}>
                <td className="font-semibold num" style={{ color: "var(--navy)" }}>{name}</td>
                <td style={{ color: "var(--slate)" }}>{{
                  fact_bookings: "one deal / booking event",
                  fact_stage_history: "one stage transition",
                  fact_arr_movement: "one ARR change event",
                  fact_gl: "one journal line",
                  fact_invoice_line: "one invoice line",
                  plan: "one plan cell (period × cost center)",
                  dim_rep: "one rep",
                  dim_account: "one account",
                }[name]}</td>
                <td className="text-right num" style={{ color: "var(--navy)" }}>{(tables[name] || []).length.toLocaleString()}</td>
                <td className="text-xs num" style={{ color: "var(--slate)" }}>{cols.slice(0, 4).join(", ")}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
